import { formatDate, formatDateWithTime } from '@folio/lib/date-formatting';
import type { ClaimManagementFieldDto } from 'korint-sdk';

type FormatContext = {
  locale: string;
  booleanLabels: { yes: string; no: string };
};

const toValidDate = (value: unknown) => {
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const countryName = (code: string, locale: string) => {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

// Repeatable groups carry every instance on their first field: not shown as a single value.
export function claimFieldItems(fields: ClaimManagementFieldDto[], context: FormatContext) {
  return fields
    .filter(field => !field.group)
    .flatMap(field => {
      const value = formatClaimFieldValue(field, context);
      return value === null ? [] : [{ id: field.key, label: field.label, value }];
    });
}

export const claimSectionItems = (
  fields: ClaimManagementFieldDto[],
  section: string,
  context: FormatContext,
) =>
  claimFieldItems(
    fields.filter(field => field.section === section),
    context,
  );

export function formatClaimFieldValue(
  {
    type,
    value,
    enumOptions,
  }: Pick<ClaimManagementFieldDto, 'type' | 'enumOptions'> & { value?: unknown },
  { locale, booleanLabels }: FormatContext,
): string | null {
  const optionLabel = (option: unknown) =>
    enumOptions?.find(({ value: optionValue }) => optionValue === String(option))?.label ??
    String(option);

  if (value === undefined || value === null || value === '') {
    return null;
  }

  switch (type) {
    case 'boolean':
      return value === true ? booleanLabels.yes : booleanLabels.no;
    case 'enum':
      return optionLabel(value);
    case 'multiselect':
      return Array.isArray(value) && value.length > 0 ? value.map(optionLabel).join(', ') : null;
    case 'date': {
      const date = toValidDate(value);
      return date ? formatDate(date, locale) : String(value);
    }
    case 'datetime': {
      const date = toValidDate(value);
      return date ? formatDateWithTime(date, locale) : String(value);
    }
    case 'country':
      return countryName(String(value), locale);
    default:
      return String(value);
  }
}
